import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { CodeBracketIcon } from '@heroicons/react/24/outline'
import {
  API_BASE_URL,
  developerAPI,
  type DeveloperKey,
  type DeveloperWebhook,
  type WebhookEvent,
} from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import ConfirmDialog from './ConfirmDialog'

const EXPIRY_OPTIONS: { days: number | null; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
  { days: null, label: 'Never' },
]

const EVENTS: { id: WebhookEvent; label: string }[] = [
  { id: 'signal.created', label: 'A call is locked' },
  { id: 'signal.closed', label: 'A call closes' },
  { id: 'content.published', label: 'A post is published' },
]

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null

const expired = (key: DeveloperKey) => !!key.expires_at && new Date(key.expires_at).getTime() < Date.now()

const usable = (key: DeveloperKey) => key.is_active && !expired(key)

/** A secret shown once, with a copy button. */
const ShownOnce = ({ label, value, onDone }: { label: string; value: string; onDone: () => void }) => {
  const [copied, setCopied] = useState(false)
  return (
    <div className="rounded-md border border-primary-200 bg-primary-50 p-3" role="status">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="mt-0.5 text-xs text-gray-600">Copy it now: it isn&apos;t shown again.</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="min-w-0 break-all rounded bg-white px-2 py-1 text-xs text-gray-900" data-testid="shown-once">
          {value}
        </code>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value)
              setCopied(true)
            } catch {
              setCopied(false)
            }
          }}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" onClick={onDone} className="text-xs text-gray-600 underline">
          Done
        </button>
      </div>
    </div>
  )
}

/** One key's webhooks: list, add, delete. */
const KeyWebhooks = ({ apiKey }: { apiKey: DeveloperKey }) => {
  const [hooks, setHooks] = useState<DeveloperWebhook[] | null>(null)
  const [url, setUrl] = useState('')
  const [creator, setCreator] = useState('')
  const [events, setEvents] = useState<WebhookEvent[]>(['signal.closed'])
  const [busy, setBusy] = useState(false)
  const [secret, setSecret] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    developerAPI
      .webhooks(apiKey.id)
      .then((list) => live && setHooks(list))
      .catch(() => live && setHooks([]))
    return () => {
      live = false
    }
  }, [apiKey.id])

  const add = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const created = await developerAPI.createWebhook(apiKey.id, {
        target_url: url.trim(),
        events,
        ...(creator.trim() ? { creator: creator.trim() } : {}),
      })
      const { secret: raw, ...hook } = created
      setHooks((prev) => [hook, ...(prev || [])])
      setSecret(raw)
      setUrl('')
      setCreator('')
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't add the webhook"))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (hook: DeveloperWebhook) => {
    try {
      await developerAPI.deleteWebhook(apiKey.id, hook.id)
      setHooks((prev) => (prev || []).filter((h) => h.id !== hook.id))
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete the webhook"))
    }
  }

  const toggleEvent = (id: WebhookEvent) =>
    setEvents((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]))

  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3" aria-label={`Webhooks for ${apiKey.name}`}>
      {secret && <ShownOnce label="Signing secret for the new webhook" value={secret} onDone={() => setSecret(null)} />}
      {hooks === null ? (
        <p className="text-xs text-gray-500">Loading webhooks…</p>
      ) : hooks.length === 0 ? (
        <p className="text-xs text-gray-500">No webhooks on this key yet.</p>
      ) : (
        <ul className="space-y-1" data-testid="webhook-list">
          {hooks.map((hook) => (
            <li key={hook.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-1.5 text-sm">
              <div className="min-w-0">
                <p className="break-all text-gray-900">{hook.target_url}</p>
                <p className="text-xs text-gray-500">
                  {hook.creator_handle ? `@${hook.creator_handle}` : 'a deleted account'} · {hook.events.join(', ')}
                  {!hook.is_active && ' · off'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove(hook)}
                aria-label={`Delete webhook to ${hook.target_url}`}
                className="text-xs text-danger-700 hover:underline"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="space-y-2">
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block text-xs text-gray-700">
            URL to send events to
            <input
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/hooks/fincreator"
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="block text-xs text-gray-700">
            Creator&apos;s handle
            <input
              value={creator}
              onChange={(e) => setCreator(e.target.value)}
              placeholder="Blank for your own account"
              maxLength={100}
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
          </label>
        </div>
        <fieldset className="flex flex-wrap gap-x-4 gap-y-1">
          <legend className="sr-only">Events</legend>
          {EVENTS.map((ev) => (
            <label key={ev.id} className="flex items-center gap-1.5 text-xs text-gray-700">
              <input type="checkbox" checked={events.includes(ev.id)} onChange={() => toggleEvent(ev.id)} />
              {ev.label} <code className="text-gray-500">{ev.id}</code>
            </label>
          ))}
        </fieldset>
        <button
          type="submit"
          disabled={busy || !url.trim() || events.length === 0}
          className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          {busy ? 'Adding…' : 'Add webhook'}
        </button>
      </form>
    </div>
  )
}

/**
 * Settings → Developer: API keys for /api/v1 (read-only) and each key's
 * webhooks. Keys and webhook secrets are shown once, at creation.
 */
const DeveloperSettings = () => {
  const [keys, setKeys] = useState<DeveloperKey[] | null>(null)
  const [name, setName] = useState('')
  const [expiry, setExpiry] = useState<number | null>(90)
  const [busy, setBusy] = useState(false)
  const [rawKey, setRawKey] = useState<string | null>(null)
  const [open, setOpen] = useState<number | null>(null)
  const [revoking, setRevoking] = useState<DeveloperKey | null>(null)
  const [revokeBusy, setRevokeBusy] = useState(false)

  useEffect(() => {
    let live = true
    developerAPI
      .keys()
      .then((list) => live && setKeys(list))
      .catch(() => live && setKeys([]))
    return () => {
      live = false
    }
  }, [])

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const { raw_key, ...key } = await developerAPI.createKey({ name: name.trim(), expires_in_days: expiry })
      setKeys((prev) => [key, ...(prev || [])])
      setRawKey(raw_key)
      setName('')
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't create the key"))
    } finally {
      setBusy(false)
    }
  }

  const revoke = async () => {
    if (!revoking) return
    setRevokeBusy(true)
    try {
      await developerAPI.revokeKey(revoking.id)
      setKeys((prev) => (prev || []).map((k) => (k.id === revoking.id ? { ...k, is_active: false } : k)))
      if (open === revoking.id) setOpen(null)
      setRevoking(null)
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't revoke the key"))
    } finally {
      setRevokeBusy(false)
    }
  }

  return (
    <section className="bg-white rounded-lg shadow p-6" aria-labelledby="developer-heading">
      <div className="mb-2 flex items-center gap-2">
        <CodeBracketIcon className="h-5 w-5 text-primary-600" />
        <h2 id="developer-heading" className="text-lg font-semibold text-gray-900">
          Developer
        </h2>
      </div>
      <p className="text-sm text-gray-600">
        Read signals and published posts from your own tools with the API at{' '}
        <code className="break-all text-gray-900">{API_BASE_URL}/api/v1</code>, sending a key in the{' '}
        <code className="text-gray-900">X-API-Key</code> header. Keys are read-only, and premium signals and posts are
        returned only where your account can see them.
      </p>

      <form onSubmit={create} className="mt-4 flex flex-wrap items-end gap-2">
        <label className="block text-sm text-gray-700">
          Key name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={100}
            placeholder="e.g. Zapier"
            className="mt-1 block w-48 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="block text-sm text-gray-700">
          Expires
          <select
            value={expiry === null ? 'never' : String(expiry)}
            onChange={(e) => setExpiry(e.target.value === 'never' ? null : Number(e.target.value))}
            className="mt-1 block rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.label} value={o.days === null ? 'never' : String(o.days)}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="rounded-md bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create key'}
        </button>
      </form>

      {rawKey && (
        <div className="mt-3">
          <ShownOnce label="Your new API key" value={rawKey} onDone={() => setRawKey(null)} />
        </div>
      )}

      {keys === null ? (
        <p className="mt-4 text-sm text-gray-500">Loading keys…</p>
      ) : keys.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">No API keys yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-100" data-testid="api-key-list">
          {keys.map((key) => {
            const live = usable(key)
            return (
              <li key={key.id} className="py-3" data-testid="api-key">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`font-medium ${live ? 'text-gray-900' : 'text-gray-500'}`}>
                      {key.name} <code className="text-xs text-gray-500">{key.key_prefix}…</code>
                      {!key.is_active ? ' · revoked' : expired(key) ? ' · expired' : ''}
                    </p>
                    <p className="text-xs text-gray-500">
                      Created {day(key.created_at)} · last used {day(key.last_used_at) || 'never'} ·{' '}
                      {key.expires_at ? `${expired(key) ? 'expired' : 'expires'} ${day(key.expires_at)}` : 'never expires'}
                    </p>
                  </div>
                  {live && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setOpen(open === key.id ? null : key.id)}
                        aria-expanded={open === key.id}
                        className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Webhooks
                      </button>
                      <button
                        type="button"
                        onClick={() => setRevoking(key)}
                        className="rounded-md px-3 py-1 text-sm text-danger-700 hover:bg-danger-50"
                      >
                        Revoke
                      </button>
                    </div>
                  )}
                </div>
                {live && open === key.id && <KeyWebhooks apiKey={key} />}
              </li>
            )
          })}
        </ul>
      )}

      <details className="mt-4 text-sm text-gray-600">
        <summary className="cursor-pointer text-gray-900">Verifying webhook deliveries</summary>
        <p className="mt-2">
          Each delivery is a JSON POST. <code>X-FinCreator-Event</code> names the event, and{' '}
          <code>X-FinCreator-Signature</code> is the HMAC-SHA256 (hex) of the raw request body, keyed with that
          webhook&apos;s secret: compute it yourself and compare before trusting the body. A delivery that doesn&apos;t
          get a 2xx response is tried up to 5 times, over about half an hour. Revoking a key turns its webhooks off.
        </p>
      </details>

      <ConfirmDialog
        isOpen={!!revoking}
        title="Revoke this key?"
        message={
          <>
            <strong>{revoking?.name}</strong> stops working at once, and its webhooks stop sending. This can&apos;t be
            undone.
          </>
        }
        confirmLabel="Revoke"
        danger
        confirming={revokeBusy}
        onConfirm={revoke}
        onCancel={() => setRevoking(null)}
      />
    </section>
  )
}

export default DeveloperSettings
